import type {
  SetTicketTourFeaturesPayload,
  TicketListParams,
} from '@/shared/api/types/ticket-types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getTicketById,
  getTickets,
  setTicketTourFeatures,
} from '@/shared/api/services/ticket-service'

export const TICKET_QUERY_KEYS = {
  all: ['tickets'] as const,
  listPrefix: () => [...TICKET_QUERY_KEYS.all, 'list'] as const,
  list: (params: TicketListParams) => [...TICKET_QUERY_KEYS.all, 'list', params] as const,
  detail: (id: string) => [...TICKET_QUERY_KEYS.all, 'detail', id] as const,
}

export function useTicketsQuery(params: TicketListParams) {
  return useQuery({
    queryKey: TICKET_QUERY_KEYS.list(params),
    queryFn: async () => getTickets(params),
    staleTime: 0,
  })
}

export function useTicketByIdQuery(id: string | undefined) {
  return useQuery({
    queryKey: TICKET_QUERY_KEYS.detail(id ?? ''),
    queryFn: async () => getTicketById(id ?? ''),
    enabled: !!id,
    staleTime: 0,
  })
}

export function useSetTicketTourFeaturesMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      ticketId,
      tourId,
      payload,
    }: {
      ticketId: string
      tourId: string
      payload: SetTicketTourFeaturesPayload
    }) => setTicketTourFeatures(ticketId, tourId, payload),
    onSuccess: (response, { ticketId }) => {
      queryClient.setQueryData(TICKET_QUERY_KEYS.detail(ticketId), response)
    },
  })
}
